import { ValueParserParams } from "ag-grid-enterprise";
import { formatPercent } from "./valueFormatters";

/**
 * Parses input for a `percent` column whose editor seeds the label's own
 * percent units ("60.54 %") rather than the stored fraction. Every numeric
 * input is percent-scaled, "%" or not, so a typed `10` is 10 % — the unit the
 * cell shows is the unit the planner types. Invalid or empty input reverts
 * instead of storing NaN.
 *
 * `decimals` is the label's. The stored fraction is unrounded (0.60539999
 * reads "60.54 %"), so confirming an untouched cell hands back a rounding of
 * `oldValue`; storing it would fire a real edit. Input equal to `oldValue`'s
 * own label keeps `oldValue`.
 */
export function percentScaledValueParser(
  params: ValueParserParams,
  decimals = 2
) {
  const parsed = readPercentScaled(params.newValue);
  if (parsed === null) {
    return params.oldValue;
  }
  const { oldValue } = params;
  const isLabelOfOldValue =
    typeof oldValue === "number" &&
    parsed === readPercentScaled(formatPercent(oldValue, decimals));
  return isLabelOfOldValue ? oldValue : parsed;
}

/** "60.54 %", "60.54" or 60.54 → 0.6054; null when there is no number. */
function readPercentScaled(raw: unknown): number | null {
  const numeric = readPercentLabel(raw);
  // toFixed + Number strips float noise (60.54 / 100 === 0.6053999999999999).
  return numeric === null ? null : Number((numeric / 100).toFixed(10));
}

/**
 * The number a percent label shows: "60.54 %", "60.54" or 60.54 → 60.54.
 * Null when there is no number. Does not rescale — the caller knows whether
 * the column stores a fraction or percent units.
 */
export function readPercentLabel(raw: unknown): number | null {
  if (typeof raw === "number") {
    return Number.isNaN(raw) ? null : raw;
  }
  if (typeof raw !== "string") {
    return null;
  }
  const digits = raw.trim().replace(/%$/, "").trim();
  const numeric = digits === "" ? NaN : Number(digits);
  return Number.isNaN(numeric) ? null : numeric;
}

/**
 * Keeps `parsed` unless it's nothing but the editor's own truncation of
 * `oldValue` being reconfirmed untouched — in which case the edit reverts to
 * `oldValue` instead of writing the truncation as a real change.
 *
 * agNumberCellEditor's `precision` TRUNCATES the value it seeds the input
 * with, so confirming an untouched cell would otherwise store the truncation
 * (0.60539999 -> 0.6053) and fire a real edit. The guard only trips where the
 * editor would in fact have truncated, so a genuine edit to that many
 * decimals still lands.
 *
 * A `"%"`-suffixed `newValue` always bypasses the guard: a typed label is a
 * deliberate entry even when it coincides with the truncation — a real edit,
 * not the editor's no-op reconfirm. Every
 * `+/- Var` / precision-capped percent cell in the app shares this exact
 * pairing, so it belongs here rather than re-implemented at each call site.
 */
export function keepUnlessEditorTruncation(
  params: Pick<ValueParserParams, "newValue" | "oldValue">,
  parsed: unknown,
  decimals: number
): unknown {
  return !isPercentScaledText(params.newValue) &&
    isEditorTruncationOf(parsed, params.oldValue, decimals)
    ? params.oldValue
    : parsed;
}

function isEditorTruncationOf(
  parsed: unknown,
  oldValue: unknown,
  decimals: number
): boolean {
  if (typeof parsed !== "number" || typeof oldValue !== "number") return false;
  const truncated = truncateLikeEditor(oldValue, decimals);
  return truncated !== oldValue && parsed === truncated;
}

/** Whether the input carries the label's "%" unit. */
export function isPercentScaledText(newValue: unknown): boolean {
  return typeof newValue === "string" && newValue.trim().endsWith("%");
}

/**
 * Parses input for a `percentUnit` column (stored value is already 0-100).
 * Strips the "%" a typed label carries ("15 %" → 15) without rescaling.
 * Empty input clears the cell instead of reverting — Delete/Backspace and an
 * emptied editor are the clear gesture on a session markdown/promotion, and
 * reverting would make the consumer see no change at all.
 */
export function percentUnitValueParser(params: ValueParserParams) {
  if (typeof params.newValue === "string" && params.newValue.trim() === "") {
    return null;
  }
  return readPercentLabel(params.newValue) ?? params.oldValue;
}

// Mirrors AgInputNumberField.adjustPrecision: ag-grid truncates the decimal
// STRING, so arithmetic truncation is not equivalent — Math.trunc(0.6053 * 1e4)
// is 6052 because the product is 6052.999999999999, which would make
// isEditorTruncationOf swallow a real 0.6053 -> 0.6052 edit.
function truncateLikeEditor(value: number, decimals: number): number {
  const [whole, fraction = ""] = String(value).split(".");
  return fraction.length <= decimals
    ? value
    : Number(`${whole}.${fraction.slice(0, decimals)}`);
}
